import { SerializedError } from '@reduxjs/toolkit';
import {
  FetchBaseQueryError,
  createApi,
  fetchBaseQuery
} from '@reduxjs/toolkit/query/react';
import { cacher } from 'app/cacheUtils/cacheIndex';
import { RootState } from 'app/store';
import { isSandboxUser } from 'common/utils/jwt-helpers';
import { isSandboxUrl } from 'common/utils/sandbox-helpers';

export interface AuthResponse {
  access_token: string;
  scope: string;
  expires_in: number;
  refresh_token: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export const authApi = createApi({
  reducerPath: 'authApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_LOGIN_URL,
    prepareHeaders: (headers, { getState }) => {
      // By default, if we have a token in the store, let's use that for authenticated requests
      const token: string | null = (getState() as RootState).auth.accessToken;
      if (token != null) {
        headers.set('authorization', `Bearer ${token}`);
      }

      return headers;
    }
  }),
  tagTypes: [...cacher.defaultTags, 'AUTHENTICATED'],
  endpoints: (builder) => ({
    login: builder.mutation<AuthResponse, FormData>({
      invalidatesTags: cacher.invalidatesUnauthorized(),
      async queryFn(request, { dispatch }, _, baseQuery) {
        const loginPromise = baseQuery({
          url: 'login',
          method: 'POST',
          body: request
        });

        const loginResult = await loginPromise;

        const loginError = loginResult.error as
          | FetchBaseQueryError
          | SerializedError
          | undefined;

        const isLoginSuccess = loginError == null;

        if (!isLoginSuccess) {
          return {
            error: loginError,
            isError: true
          } as any;
        }

        const authResponse = loginResult.data as AuthResponse;

        const accessToken = authResponse.access_token;

        const { data: jwtToken } = await dispatch(
          authApi.endpoints.authenticate.initiate(accessToken)
        );

        const isUserSandbox = isSandboxUser(jwtToken!);
        const isUrlSandbox = isSandboxUrl();

        const isInvalidDomainLogin =
          (isUserSandbox && !isUrlSandbox) || (!isUserSandbox && isUrlSandbox);

        if (isInvalidDomainLogin) {
          return {
            error: {
              originalStatus: 401,
              status: 'Error'
            },
            isError: true
          } as any;
        }

        return { data: authResponse };
      }
    }),
    authenticate: builder.query<string, string>({
      query: (accessTokenOverride?: string) => {
        const authenticateRequest = {
          url: 'authenticate',
          method: 'GET'
        };

        if (accessTokenOverride) {
          return {
            ...authenticateRequest,
            headers: new Headers({
              authorization: `Bearer ${accessTokenOverride}`
            })
          };
        }

        return authenticateRequest;
      },
      providesTags: ['AUTHENTICATED']
    })
  })
});

export const { useLoginMutation, useAuthenticateQuery } = authApi;
